import { Module } from 'vuex'
import { MiniNews } from '@/types/MiniNews'
import { NewsItem } from '@/types/News'
import { RootState } from '@/store/index'
import { getMiniNews, getNews } from '@/repositories/wpRestApiRepository'
import {
  getHospitalCount,
  getRecommendedHospitals,
  getPickupHospitals
} from '@/repositories/appApiRepository'
import { isLatestNewsWithin90Days } from '@/utils/newsUtils'
import {
  HomeHospitalItem,
  EnrichedHomeHospitalItem
} from '@/types/HomeHospital'
import { ActionHistory } from '@/types/StoreState'
import {
  getDisplayBedCount,
  getDisplayDoctorCount,
  hasSomeScore,
  getHospitalTags,
  getFeatureTags,
  getDisplayPublicationDeadline
} from '@/utils/homeHospitalUtils'

interface HomeState {
  hospitalCount: number
  miniNews: MiniNews | {}
  news: NewsItem[]
  newsTopics: NewsItem[]
  recommendedHospitals: HomeHospitalItem[]
  pickupHospitals: HomeHospitalItem[]
}

const homeState = {
  hospitalCount: 0,
  miniNews: {},
  news: [],
  newsTopics: [],
  recommendedHospitals: [],
  pickupHospitals: []
}

export const home: Module<HomeState, RootState> = {
  namespaced: true,

  state: homeState,

  mutations: {
    setHospitalCount(state: HomeState, payload: number) {
      state.hospitalCount = payload
    },
    setMiniNews(state: HomeState, payload: MiniNews) {
      state.miniNews = { ...payload }
    },
    setNews(state: HomeState, payload: NewsItem[]) {
      state.news = payload
    },
    setNewsTopics(state: HomeState, payload: NewsItem[]) {
      state.newsTopics = payload
    },
    setRecommendedHospitals(state: HomeState, payload: HomeHospitalItem[]) {
      state.recommendedHospitals = payload
    },
    setPickupHospitals(state: HomeState, payload: HomeHospitalItem[]) {
      state.pickupHospitals = payload
    }
  },

  actions: {
    async loadEvents({ dispatch }) {
      await dispatch('events/loadEvents', { perPage: 10 }, { root: true })
    },
    async loadHospitalCount({ commit }) {
      const res = await getHospitalCount()
      commit('setHospitalCount', res.data.totalCount)
    },
    async loadMiniNews({ commit }) {
      const res = await getMiniNews()
      commit('setMiniNews', res.data)
    },
    async loadNews({ commit }) {
      const res = await getNews()
      commit('setNews', res.data)

      if (isLatestNewsWithin90Days(res.data)) {
        commit('setNewsTopics', res.data)
      }
    },
    async loadRecommendedHospitals(
      { commit },
      { areaId }: { areaId?: string } = {}
    ) {
      const res = await getRecommendedHospitals(areaId)
      commit('setRecommendedHospitals', res.data)
    },
    async loadPickupHospitals({ commit }) {
      const res = await getPickupHospitals()
      commit('setPickupHospitals', res.data)
    }
  },

  getters: {
    getNews(state): NewsItem[] {
      return state.news
    },
    getNewsTopics(state): NewsItem[] {
      return state.newsTopics
    },
    getEvents(state, getters, rootState, rootGetters) {
      return rootGetters['events/getEvents']
    },
    // NOTE: 表示用の項目をデータ取得層ではなくstore層で変換する例。少し冗長かもしれない・・
    getEnrichedRecommendedHospitals(state): EnrichedHomeHospitalItem[] {
      return state.recommendedHospitals.map((hospital) => {
        return {
          ...hospital,
          displayBedCount: getDisplayBedCount(hospital.bedCount),
          displayDoctorCount: getDisplayDoctorCount(hospital.doctorCount),
          shouldShowScore: hasSomeScore(hospital),
          hospitalTags: getHospitalTags(hospital),
          featureTags: getFeatureTags(hospital.discerningConditions),
          hospitalScores: [
            {
              label: '症例数',
              rate: hospital.caseScore
            },
            {
              label: '指導医数',
              rate: hospital.instructorScore
            },
            {
              label: '給与',
              rate: hospital.salaryScore
            },
            {
              label: '倍率',
              rate: hospital.magnificationScore
            }
          ],
          displayPublicationDeadline: getDisplayPublicationDeadline(
            hospital.publicationDeadline
          )
        }
      })
    },
    getEnrichedPickupHospitals(state): EnrichedHomeHospitalItem[] {
      return state.pickupHospitals.map((hospital) => {
        return {
          ...hospital,
          displayBedCount: getDisplayBedCount(hospital.bedCount),
          displayDoctorCount: getDisplayDoctorCount(hospital.doctorCount),
          shouldShowScore: hasSomeScore(hospital),
          hospitalTags: getHospitalTags(hospital),
          featureTags: getFeatureTags(hospital.discerningConditions),
          hospitalScores: [
            {
              label: '症例数',
              rate: hospital.caseScore
            },
            {
              label: '指導医数',
              rate: hospital.instructorScore
            },
            {
              label: '給与',
              rate: hospital.salaryScore
            },
            {
              label: '倍率',
              rate: hospital.magnificationScore
            }
          ],
          displayPublicationDeadline: getDisplayPublicationDeadline(
            hospital.publicationDeadline
          )
        }
      })
    },
    isAlreadyFavorite: (state, getters, rootState) => (hospitalId: number) => {
      if (!rootState.isLogin) return false
      return rootState.userInfo.favorite_history.some((el: ActionHistory) => {
        return el.hospital_id == hospitalId
      })
    },
    isParticipated: (state, getters, rootState) => (hospitalId: number) => {
      return rootState.eventParticipatingHospitals.includes(`${hospitalId}`)
    }
  }
}
